import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/landingData";
import { Loader2, Sparkles, MailCheck } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook } from "react-icons/fa";

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [magicMode, setMagicMode] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSending, setMagicSending] = useState(false);
  const [magicSentTo, setMagicSentTo] = useState("");

  useEffect(() => {
    if (user) navigate(user.onboarded ? "/app" : "/onboarding", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = isLogin
        ? await login(form.email, form.password)
        : await register(form.email, form.password, form.name);
      toast.success(isLogin ? "Welcome back." : "Welcome to b→come.");
      navigate(u.onboarded ? "/app" : "/onboarding");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Authentication failed."));
    } finally {
      setLoading(false);
    }
  };

  const sendMagicLink = async (e) => {
    e.preventDefault();
    const email = magicEmail.trim().toLowerCase();
    if (!email) return;
    setMagicSending(true);
    try {
      await api.post("/auth/magic/request", { email });
      setMagicSentTo(email);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Could not send the link. Try again."));
    } finally {
      setMagicSending(false);
    }
  };

  const google = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const facebook = () => {
    toast.info("Facebook login needs a Facebook App ID/Secret to activate. Ask the team to add it.");
  };

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center px-5 py-16">
      <div className="pointer-events-none absolute -left-40 top-0 h-[480px] w-[480px] glow-violet blur-3xl opacity-40" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[480px] w-[480px] glow-orange blur-3xl opacity-30" />

      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A0C] p-8 sm:p-10">
        <Link to="/" className="mb-8 flex justify-center">
          <img src={LOGO_URL} alt="b→come" className="h-12 w-auto" />
        </Link>
        <h1 className="text-center font-mega text-3xl text-white">
          {isLogin ? "Welcome Back" : "Start Becoming"}
        </h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          {isLogin ? "Sign in to continue your journey." : "Create your account. No credit card required."}
        </p>

        <div className="mt-8 grid grid-cols-2 gap-3">
          <button onClick={google} data-testid="google-btn"
            className="flex items-center justify-center gap-2 rounded-full border border-white/12 bg-white/[0.03] py-3 text-sm font-medium text-white transition-colors hover:border-white/30">
            <FcGoogle className="h-5 w-5" /> Google
          </button>
          <button onClick={facebook} data-testid="facebook-btn"
            className="flex items-center justify-center gap-2 rounded-full border border-white/12 bg-white/[0.03] py-3 text-sm font-medium text-white transition-colors hover:border-white/30">
            <FaFacebook className="h-5 w-5 text-[#1877F2]" /> Facebook
          </button>
        </div>

        <div className="my-6 flex items-center gap-4 text-xs uppercase tracking-widest text-white/40">
          <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
        </div>

        {magicMode ? (
          magicSentTo ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center" data-testid="magic-sent-panel">
              <MailCheck className="mx-auto h-8 w-8 text-gold" />
              <h2 className="mt-3 font-nunito text-lg font-bold text-white">Check your inbox</h2>
              <p className="mt-2 text-sm text-white/60">
                We sent a one-time sign-in link to <span className="font-semibold text-white">{magicSentTo}</span>.
                It expires in 15 minutes.
              </p>
              <button type="button" onClick={() => { setMagicSentTo(""); setMagicEmail(""); }}
                data-testid="magic-send-another"
                className="mt-5 text-sm font-semibold text-gold underline-offset-4 hover:underline">
                Send to a different email
              </button>
            </div>
          ) : (
            <form onSubmit={sendMagicLink} className="space-y-3" data-testid="magic-form">
              <label className="block text-xs font-semibold uppercase tracking-widest text-white/50">
                Email me a sign-in link
              </label>
              <input required type="email" value={magicEmail} onChange={(e) => setMagicEmail(e.target.value)}
                placeholder="you@example.com" data-testid="magic-email-input"
                className="h-12 w-full rounded-xl border border-white/12 bg-[#0C0C0E] px-4 text-white outline-none focus:border-primary" />
              <button type="submit" disabled={magicSending} data-testid="magic-send-btn"
                className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-gradient-brand font-bold uppercase tracking-wide text-black transition-all hover:scale-[1.02] disabled:opacity-70">
                {magicSending ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Email me a link <Sparkles className="h-4 w-4" /></>}
              </button>
              <button type="button" onClick={() => setMagicMode(false)} data-testid="magic-use-password-btn"
                className="w-full text-center text-xs font-semibold uppercase tracking-widest text-white/50 hover:text-white/80">
                Use password instead
              </button>
            </form>
          )
        ) : (
          <>
            <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
              {!isLogin && (
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Full name" data-testid="name-input"
                  className="h-12 w-full rounded-xl border border-white/12 bg-[#0C0C0E] px-4 text-white outline-none focus:border-primary" />
              )}
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email" data-testid="email-input"
                className="h-12 w-full rounded-xl border border-white/12 bg-[#0C0C0E] px-4 text-white outline-none focus:border-primary" />
              <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password" data-testid="password-input"
                className="h-12 w-full rounded-xl border border-white/12 bg-[#0C0C0E] px-4 text-white outline-none focus:border-primary" />
              <button type="submit" disabled={loading} data-testid="submit-btn"
                className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-gradient-brand font-bold uppercase tracking-wide text-black transition-all hover:scale-[1.02] disabled:opacity-70">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : isLogin ? "Sign In" : "Create Account"}
              </button>
            </form>
            <button type="button" onClick={() => { setMagicMode(true); setMagicEmail(form.email); }}
              data-testid="magic-mode-btn"
              className="mt-4 flex w-full items-center justify-center gap-2 text-sm font-semibold text-gold hover:underline">
              <Sparkles className="h-4 w-4" /> Email me a magic link — no password
            </button>
          </>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {isLogin ? "New to b→come? " : "Already have an account? "}
          <Link to={isLogin ? "/register" : "/login"} className="font-semibold text-gold">
            {isLogin ? "Start free" : "Sign in"}
          </Link>
        </p>
      </div>
    </div>
  );
}
